import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { FC, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../states/containers/store'
import { getGetPrams } from '../../../utils/url'
import {
  fetchTaskLabelGroupSummaries,
  fetchTaskViewableLabelGroup,
  fetchTaskViewableLabelGroupMember,
  fetchTaskViewableLabelGroupTeam,
} from '../../../states/containers/task/taskSlice'
import LoadingSquare from '../common/LoadingSquare'
import COLOR_LIST from '../../constants/taskColor'
import { quoteSplit } from '../../../utils/string'

interface Team {
  id: number
  name: string
  code?: string | null
}

interface Member {
  id: number
  name: string
  code?: string | null
}

interface LabelGroup {
  id: number
  name: string
  code?: string | null
  color?: string
}

interface MasterRelationLabelGroup {
  callbackIds: (ids: number[]) => void
  list: { id: number, name: string }[]
  disableState: boolean
  callbackDisable: (changed: boolean) => void
}

interface Props {
  callbackLabelMasterKeywords?: (keyword: string[]) => void
  callbackTeamIds?: (teamIds: number[]) => void
  callbackMemberIds?: (memberIds: number[]) => void
  callbackLabelGroupIds?: (labelGroupIds: number[]) => void
  callbackColors?: (colors: string[]) => void
  labelMasterKeywords?: string[]
  memberList?: { id: number, name: string }[]
  teamList?: { id: number, name: string }[]
  labelGroupList?: { id: number, name: string }[]
  colors?: string[]
  isClear?: boolean
  masterRelationLabelGroup?: MasterRelationLabelGroup
}

interface GetParams {
  label_master_keywords: string
  team_ids: string
  member_ids: string
  label_group_ids: string
  master_relation_label_group_ids: string
  colors: string
}

const SEARCH_LIMIT = 1000

const SearchModuleForHighVolume: FC<Props> = (props) => {
  const {
    callbackLabelMasterKeywords,
    callbackTeamIds,
    callbackMemberIds,
    callbackLabelGroupIds,
    callbackColors,
    labelMasterKeywords,
    memberList,
    teamList,
    labelGroupList,
    colors,
    isClear,
    masterRelationLabelGroup,
  } = props

  const {
    task,
  } = useSelector((state: RootState) => state)

  const dispatch = useDispatch()

  const [isSearchDisplay, setIsSearchDisplay] = useState(false)
  const [isClickable, setIsClickable] = useState(true)

  const [selectLabelMasterKeyword, setSelectLabelMasterKeyword] = useState<string>("")

  const [selectTeamList, setSelectTeamList] = useState<Team[]>([])
  const [selectMemberList, setSelectMemberList] = useState<Member[]>([])
  const [selectLabelGroupList, setSelectLabelGroupList] = useState<LabelGroup[]>([])
  const [selectMasterRelationLabelGroupList, setSelectMasterRelationLabelGroupList] = useState<LabelGroup[]>([])
  const [selectMasterRelationLabelGroupDisable, setSelectMasterRelationLabelGroupDisable] = useState<boolean>(false)
  const [selectColorList, setSelectColorList] = useState<string[]>([])

  const [searchTeamWord, setSearchTeamWord] = useState<string>("")
  const [searchMemberWord, setSearchMemberWord] = useState<string>("")
  const [searchLabelGroupWord, setSearchLabelGroupWord] = useState<string>("")
  const [searchMasterRelationLabelGroupWord, setSearchMasterRelationLabelGroupWord] = useState<string>("")

  const [isTeamWordLoading, setIsTeamWordLoading] = useState<boolean>(false)
  const [isMemberWordLoading, setIsMemberWordLoading] = useState<boolean>(false)
  const [isLabelGroupWordLoading, setIsLabelGroupWordLoading] = useState<boolean>(false)
  const [isMasterRelationLabelGroupWordLoading, setIsMasterRelationLabelGroupWordLoading] = useState<boolean>(false)

  const [teamComposition, setTeamComposition] = useState<boolean>(false)
  const [memberComposition, setMemberComposition] = useState<boolean>(false)
  const [labelGroupComposition, setLabelGroupComposition] = useState<boolean>(false)
  const [masterRelationLabelGroupComposition, setMasterRelationLabelGroupComposition] = useState<boolean>(false)

  const [isTeamSearched, setIsTeamSearched] = useState<boolean>(false)
  const [isMemberSearched, setIsMemberSearched] = useState<boolean>(false)
  const [isLabelGroupSearched, setIsLabelGroupSearched] = useState<boolean>(false)
  const [isMasterRelationLabelGroupSearched, setIsMasterRelationLabelGroupSearched] = useState<boolean>(false)

  const [searchedTeamList, setSearchedTeamList] = useState<Team[]>([])
  const [searchedMemberList, setSearchedMemberList] = useState<Member[]>([])
  const [searchedLabelGroupList, setSearchedLabelGroupList] = useState<LabelGroup[]>([])
  const [searchedMasterRelationLabelGroupList, setSearchedMasterRelationLabelGroupList] = useState<LabelGroup[]>([])

  const clickTeam = (team: Team) => {
    const currentIds = selectTeamList.map((v) => v.id)
    if (currentIds.includes(team.id)) {
      setSelectTeamList((cur) => cur.filter((v) => v.id !== team.id))
    } else {
      setSelectTeamList((cur) => [...cur, team])
    }
  }

  const clickMember = (member: Member) => {
    const currentIds = selectMemberList.map((v) => v.id)
    if (currentIds.includes(member.id)) {
      setSelectMemberList((cur) => cur.filter((v) => v.id !== member.id))
    } else {
      setSelectMemberList((cur) => [...cur, member])
    }
  }

  const clickLabelGroup = (labelGroup: LabelGroup) => {
    const currentIds = selectLabelGroupList.map((v) => v.id)
    if (currentIds.includes(labelGroup.id)) {
      setSelectLabelGroupList((cur) => cur.filter((v) => v.id !== labelGroup.id))
    } else {
      setSelectLabelGroupList((cur) => [...cur, labelGroup])
    }
  }

  const clickMasterRelationLabelGroup = (labelGroup: LabelGroup) => {
    const currentIds = selectMasterRelationLabelGroupList.map((v) => v.id)
    if (currentIds.includes(labelGroup.id)) {
      setSelectMasterRelationLabelGroupList((cur) => cur.filter((v) => v.id !== labelGroup.id))
    } else {
      setSelectMasterRelationLabelGroupList((cur) => [...cur, labelGroup])
    }
  }

  const clickColor = (color: string) => {
    if (selectColorList.includes(color)) {
      setSelectColorList(selectColorList.filter((code) => code !== color))
    } else {
      setSelectColorList([...selectColorList, color])
    }
  }

  const openSearch = () => {
    setIsSearchDisplay(true)
  }

  const cancelSearch = () => {
    setIsSearchDisplay(false)
    setSearchTeamWord("")
    setSearchMemberWord("")
    setSearchLabelGroupWord("")
    setSearchMasterRelationLabelGroupWord("")
  }

  const resetSearch = () => {
    setIsSearchDisplay(false)
    setSearchTeamWord("")
    setSearchMemberWord("")
    setSearchLabelGroupWord("")
    setSearchMasterRelationLabelGroupWord("")
    setSelectLabelMasterKeyword("")
    setSelectTeamList([])
    setSelectMemberList([])
    setSelectLabelGroupList([])
    setSelectMasterRelationLabelGroupList([])
    setSelectMasterRelationLabelGroupDisable(false)
    setSelectColorList([])
    if (callbackLabelMasterKeywords) callbackLabelMasterKeywords([])
    if (callbackTeamIds) callbackTeamIds([])
    if (callbackMemberIds) callbackMemberIds([])
    if (callbackLabelGroupIds) callbackLabelGroupIds([])
    if (masterRelationLabelGroup) {
      masterRelationLabelGroup.callbackIds([])
      masterRelationLabelGroup.callbackDisable(false)
    }
    if (callbackColors) callbackColors([])
  }

  const submitSearch = () => {
    const getParams = getGetPrams(location.search) as GetParams
    setIsSearchDisplay(false)
    const getParamLabelMasterKeywords = getParams.label_master_keywords ? getParams.label_master_keywords.split(',') : []
    const tmpLabelMasterKeywords = quoteSplit(selectLabelMasterKeyword).filter((v) => v)
    if (
      callbackLabelMasterKeywords && (
        (getParamLabelMasterKeywords.filter((value) => tmpLabelMasterKeywords.indexOf(value) === -1).length)
        || (tmpLabelMasterKeywords.filter((value) => getParamLabelMasterKeywords.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelMasterKeywords(tmpLabelMasterKeywords)
    }
    const getParamTeamIds = getParams.team_ids ? getParams.team_ids.split(',').map((v) => Number(v)) : []
    const selectTeamIds = selectTeamList.map((v) => v.id)
    if (
      callbackTeamIds && (
        (getParamTeamIds.filter((value) => selectTeamIds.indexOf(value) === -1).length)
        || (selectTeamIds.filter((value) => getParamTeamIds.indexOf(value) === -1).length)
      )
    ) {
      callbackTeamIds(selectTeamIds)
    }
    const getParamMemberIds = getParams.member_ids ? getParams.member_ids.split(',').map((v) => Number(v)) : []
    const selectMemberIds = selectMemberList.map((v) => v.id)
    if (
      callbackMemberIds && (
        (getParamMemberIds.filter((value) => selectMemberIds.indexOf(value) === -1).length)
        || (selectMemberIds.filter((value) => getParamMemberIds.indexOf(value) === -1).length)
      )
    ) {
      callbackMemberIds(selectMemberIds)
    }
    const getParamLabelGroupIds = getParams.label_group_ids ? getParams.label_group_ids.split(',').map((v) => Number(v)) : []
    const selectLabelGroupIds = selectLabelGroupList.map((v) => v.id)
    if (
      callbackLabelGroupIds && (
        (getParamLabelGroupIds.filter((value) => selectLabelGroupIds.indexOf(value) === -1).length)
        || (selectLabelGroupIds.filter((value) => getParamLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      callbackLabelGroupIds(selectLabelGroupIds)
    }
    const getParamMasterRelationLabelGroupIds = getParams.master_relation_label_group_ids
      ? getParams.master_relation_label_group_ids.split(',').map((v) => Number(v))
      : []
    const selectMasterRelationLabelGroupIds = selectMasterRelationLabelGroupList.map((v) => v.id)
    if (
      masterRelationLabelGroup && (
        (getParamMasterRelationLabelGroupIds.filter((value) => selectMasterRelationLabelGroupIds.indexOf(value) === -1).length)
        || (selectMasterRelationLabelGroupIds.filter((value) => getParamMasterRelationLabelGroupIds.indexOf(value) === -1).length)
      )
    ) {
      masterRelationLabelGroup.callbackIds(selectMasterRelationLabelGroupIds)
    }
    if (masterRelationLabelGroup) {
      masterRelationLabelGroup.callbackDisable(selectMasterRelationLabelGroupDisable)
    }
    if (callbackColors) {
      callbackColors(selectColorList)
    }
    setSearchTeamWord("")
    setSearchMemberWord("")
    setSearchLabelGroupWord("")
    setSearchMasterRelationLabelGroupWord("")
  }

  const searchTeam = () => {
    const words = searchTeamWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    if (!words.length) {
      setSearchedTeamList([])
      return
    }
    setIsTeamWordLoading(true)
    dispatch(
      fetchTaskViewableLabelGroupTeam(
        {
          keywords: words,
          limit: SEARCH_LIMIT,
        },
        (result) => {
          setSearchedTeamList(result.teams)
          setIsTeamWordLoading(false)
          setIsTeamSearched(true)
        },
      ),
    )
  }

  const searchMember = () => {
    const words = searchMemberWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    if (!words.length) {
      setSearchedMemberList([])
      return
    }
    setIsMemberWordLoading(true)
    dispatch(
      fetchTaskViewableLabelGroupMember(
        {
          keywords: words,
          limit: SEARCH_LIMIT,
        },
        (result) => {
          setSearchedMemberList(result.members)
          setIsMemberWordLoading(false)
          setIsMemberSearched(true)
        },
      ),
    )
  }

  const searchLabelGroup = () => {
    const words = searchLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    if (!words.length) {
      setSearchedLabelGroupList([])
      return
    }
    setIsLabelGroupWordLoading(true)
    dispatch(
      fetchTaskViewableLabelGroup(
        {
          keywords: words,
          limit: SEARCH_LIMIT,
        },
        (result) => {
          setSearchedLabelGroupList(result.label_groups)
          setIsLabelGroupWordLoading(false)
          setIsLabelGroupSearched(true)
        },
      ),
    )
  }

  const searchMasterRelationLabelGroup = () => {
    const words = searchMasterRelationLabelGroupWord.split(/\s+/).filter(Boolean).map((v) => v.toLowerCase())
    if (!words.length) {
      setSearchedMasterRelationLabelGroupList([])
      return
    }
    setIsMasterRelationLabelGroupWordLoading(true)
    dispatch(
      fetchTaskLabelGroupSummaries(
        {
          keywords: words,
          limit: SEARCH_LIMIT,
        },
        (result) => {
          setSearchedMasterRelationLabelGroupList(result.label_groups)
          setIsMasterRelationLabelGroupWordLoading(false)
          setIsMasterRelationLabelGroupSearched(true)
        },
      ),
    )
  }

  useEffect(() => {
    const html = document.querySelector('html')
    if (!html) return
    if (isSearchDisplay) {
      html.classList.add('modal_open')
    } else {
      html.classList.remove('modal_open')
    }
  }, [isSearchDisplay])

  useEffect(() => {
    if (!isClear) return
    setSelectLabelMasterKeyword("")
    setSelectTeamList([])
    setSelectMemberList([])
    setSelectLabelGroupList([])
    setSelectMasterRelationLabelGroupList([])
    setSelectMasterRelationLabelGroupDisable(false)
    setSelectColorList([])
  }, [isClear])

  useEffect(() => {
    setIsClickable(!task.isLoading)
  }, [task.isLoading])

  useEffect(() => {
    if (!labelMasterKeywords) return
    setSelectLabelMasterKeyword(labelMasterKeywords.join(' '))
  }, [labelMasterKeywords])

  useEffect(() => {
    if (!memberList) return
    setSelectMemberList(memberList)
  }, [memberList])

  useEffect(() => {
    if (!teamList) return
    setSelectTeamList(teamList)
  }, [teamList])

  useEffect(() => {
    if (!labelGroupList) return
    setSelectLabelGroupList(labelGroupList)
  }, [labelGroupList])

  useEffect(() => {
    if (!masterRelationLabelGroup?.list) return
    setSelectMasterRelationLabelGroupList(masterRelationLabelGroup.list)
  }, [masterRelationLabelGroup?.list])

  useEffect(() => {
    if (!masterRelationLabelGroup) return
    setSelectMasterRelationLabelGroupDisable(masterRelationLabelGroup.disableState)
  }, [masterRelationLabelGroup?.disableState])

  useEffect(() => {
    if (!colors) return
    setSelectColorList(colors)
  }, [colors])

  useEffect(() => {
    setIsTeamSearched(false)
    const timer = setTimeout(() => {
      if (!teamComposition) {
        searchTeam()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchTeamWord, teamComposition])

  useEffect(() => {
    setIsMemberSearched(false)
    const timer = setTimeout(() => {
      if (!memberComposition) {
        searchMember()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchMemberWord, memberComposition])

  useEffect(() => {
    setIsLabelGroupSearched(false)
    const timer = setTimeout(() => {
      if (!labelGroupComposition) {
        searchLabelGroup()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchLabelGroupWord, labelGroupComposition])

  useEffect(() => {
    setIsMasterRelationLabelGroupSearched(false)
    const timer = setTimeout(() => {
      if (!masterRelationLabelGroupComposition) {
        searchMasterRelationLabelGroup()
      }
    }, 700)
    return () => clearTimeout(timer)
  }, [searchMasterRelationLabelGroupWord, masterRelationLabelGroupComposition])

  return (
    <>
      <button
        className="search_button"
        type="button"
        onClick={() => openSearch()}
        disabled={!isClickable}
      >
        <FontAwesomeIcon icon={['fas', 'search']} />
        &nbsp;
        絞り込み
      </button>
      <div className={`search_box ${isSearchDisplay || 'none'}`}>
        <div className="search_detail">
          {
            callbackLabelMasterKeywords ? (
              <div className="category">
                <div className="category_title">
                  業務名で絞り込み
                  {
                    selectLabelMasterKeyword ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setSelectLabelMasterKeyword("")}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <input
                  type="text"
                  placeholder="検索したい業務名を入力してください"
                  onChange={(event) => setSelectLabelMasterKeyword(event.target.value)}
                  value={selectLabelMasterKeyword}
                  style={{
                    width: "100%",
                  }}
                />
              </div>
            ) : (
              <></>
            )
          }
          {
            callbackTeamIds && (
              <div className="category">
                <div className="category_title">
                  チームで絞り込み
                </div>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="キーワードでチームを検索・選択してください"
                    onChange={(event) => setSearchTeamWord(event.target.value)}
                    onCompositionStart={() => setTeamComposition(true)}
                    onCompositionEnd={() => setTeamComposition(false)}
                    value={searchTeamWord}
                    style={{
                      width: "100%",
                    }}
                  />
                  {
                    !!searchTeamWord.length && (
                      <button
                        type="button"
                        className="reset"
                        onClick={() => setSearchTeamWord("")}
                      >
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                {
                  !!selectTeamList.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        marginTop: "8px",
                        padding: "4px",
                      }}
                    >
                      <div
                        style={{
                          padding: "4px",
                          borderBottom: "dashed 1px #CCCCCC",
                        }}
                      >
                        選択済み
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          padding: "4px",
                        }}
                      >
                        {
                          selectTeamList.map((v) => (
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "20px",
                                padding: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                fontSize: "10px",
                              }}
                            >
                              {v.name}
                              <button
                                type="button"
                                onClick={() => setSelectTeamList((cur) => cur.filter((w) => w.id !== v.id))}
                                style={{
                                  padding: "0",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  isTeamWordLoading ? (
                    <LoadingSquare>
                      <>
                        チームデータを取得しています…
                      </>
                    </LoadingSquare>
                  ) : (
                    <>
                      {
                        !searchedTeamList.filter((v) => !selectTeamList.map((w) => w.id).includes(v.id)).length ? (
                          <>
                            <div
                              style={{
                                padding: "16px",
                                backgroundColor: "#f5f7ff",
                                textAlign: "center",
                                fontSize: "16px",
                                color: "#999999",
                                marginTop: "8px",
                              }}
                            >
                              {
                                searchTeamWord.length ? (
                                  <>
                                    {
                                      isTeamSearched ? (
                                        "キーワードに該当するチームがありませんでした"
                                      ) : (
                                        "検索中…"
                                      )
                                    }
                                  </>
                                ) : (
                                  "キーワードを入力して、チームを検索してください"
                                )
                              }
                            </div>
                          </>
                        ) : (
                          <>
                            {
                              searchedTeamList.length === SEARCH_LIMIT && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    alignItems: "center",
                                    gap: "4px",
                                    border: "solid 1px #FF0000",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff2f2",
                                    color: "#FF0000",
                                    padding: "8px",
                                    marginBottom: "8px",
                                    marginTop: "8px",
                                  }}
                                >
                                  <div>
                                    {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                  </div>
                                  <div>
                                    お探しのチームが見つからない時は、キーワードを詳細に入力してください
                                  </div>
                                </div>
                              )
                            }
                            <div className="chip_list" style={{ marginTop: "8px" }}>
                              {
                                searchedTeamList.filter((v) => !selectTeamList.map((w) => w.id).includes(v.id)).map((v) => (
                                  <button
                                    type="button"
                                    className="chip"
                                    key={`labelGroup_${v.id}`}
                                    onClick={() => clickTeam(v)}
                                  >
                                    {v.name}
                                  </button>
                                ))
                              }
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </div>
            )
          }
          {
            callbackMemberIds && (
              <div className="category">
                <div className="category_title">
                  ユーザーで絞り込み
                </div>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="キーワードでユーザーを検索・選択してください"
                    onChange={(event) => setSearchMemberWord(event.target.value)}
                    onCompositionStart={() => setMemberComposition(true)}
                    onCompositionEnd={() => setMemberComposition(false)}
                    value={searchMemberWord}
                    style={{
                      width: "100%",
                    }}
                  />
                  {
                    !!searchMemberWord.length && (
                      <button
                        type="button"
                        className="reset"
                        onClick={() => setSearchMemberWord("")}
                      >
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                {
                  !!selectMemberList.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        marginTop: "8px",
                        padding: "4px",
                      }}
                    >
                      <div
                        style={{
                          padding: "4px",
                          borderBottom: "dashed 1px #CCCCCC",
                        }}
                      >
                        選択済み
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          padding: "4px",
                        }}
                      >
                        {
                          selectMemberList.map((v) => (
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "20px",
                                padding: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                fontSize: "10px",
                              }}
                            >
                              {v.name}
                              <button
                                type="button"
                                onClick={() => setSelectMemberList((cur) => cur.filter((w) => w.id !== v.id))}
                                style={{
                                  padding: "0",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  isMemberWordLoading ? (
                    <LoadingSquare>
                      <>
                        ユーザーデータを取得しています…
                      </>
                    </LoadingSquare>
                  ) : (
                    <>
                      {
                        !searchedMemberList.filter((v) => !selectMemberList.map((w) => w.id).includes(v.id)).length ? (
                          <>
                            <div
                              style={{
                                padding: "16px",
                                backgroundColor: "#f5f7ff",
                                textAlign: "center",
                                fontSize: "16px",
                                color: "#999999",
                                marginTop: "8px",
                              }}
                            >
                              {
                                searchMemberWord.length ? (
                                  <>
                                    {
                                      isMemberSearched ? (
                                        "キーワードに該当するユーザーがありませんでした"
                                      ) : (
                                        "検索中…"
                                      )
                                    }
                                  </>
                                ) : (
                                  "キーワードを入力して、ユーザーを検索してください"
                                )
                              }
                            </div>
                          </>
                        ) : (
                          <>
                            {
                              searchedMemberList.length === SEARCH_LIMIT && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    alignItems: "center",
                                    gap: "4px",
                                    border: "solid 1px #FF0000",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff2f2",
                                    color: "#FF0000",
                                    padding: "8px",
                                    marginBottom: "8px",
                                    marginTop: "8px",
                                  }}
                                >
                                  <div>
                                    {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                  </div>
                                  <div>
                                    お探しのユーザーが見つからない時は、キーワードを詳細に入力してください
                                  </div>
                                </div>
                              )
                            }
                            <div className="chip_list" style={{ marginTop: "8px" }}>
                              {
                                searchedMemberList.filter((v) => !selectMemberList.map((w) => w.id).includes(v.id)).map((v) => (
                                  <button
                                    type="button"
                                    className="chip"
                                    key={`labelGroup_${v.id}`}
                                    onClick={() => clickMember(v)}
                                  >
                                    {v.name}
                                  </button>
                                ))
                              }
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </div>
            )
          }
          {
            callbackLabelGroupIds && (
              <div className="category">
                <div className="category_title">
                  プロジェクト名・プロジェクトコードで絞り込み
                </div>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="キーワードでプロジェクト名を検索・選択してください"
                    onChange={(event) => setSearchLabelGroupWord(event.target.value)}
                    onCompositionStart={() => setLabelGroupComposition(true)}
                    onCompositionEnd={() => setLabelGroupComposition(false)}
                    value={searchLabelGroupWord}
                    style={{
                      width: "100%",
                    }}
                  />
                  {
                    !!searchLabelGroupWord.length && (
                      <button
                        type="button"
                        className="reset"
                        onClick={() => setSearchLabelGroupWord("")}
                      >
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                {
                  !!selectLabelGroupList.length && (
                    <div
                      style={{
                        border: "solid 1px #CCCCCC",
                        borderRadius: "4px",
                        marginTop: "8px",
                        padding: "4px",
                      }}
                    >
                      <div
                        style={{
                          padding: "4px",
                          borderBottom: "dashed 1px #CCCCCC",
                        }}
                      >
                        選択済み
                      </div>
                      <div
                        style={{
                          display: "flex",
                          flexWrap: "wrap",
                          gap: "4px",
                          padding: "4px",
                        }}
                      >
                        {
                          selectLabelGroupList.map((v) => (
                            <div
                              style={{
                                border: "solid 1px #CCCCCC",
                                borderRadius: "20px",
                                padding: "2px 4px",
                                display: "flex",
                                alignItems: "center",
                                gap: "2px",
                                fontSize: "10px",
                              }}
                            >
                              {v.name}
                              <button
                                type="button"
                                onClick={() => setSelectLabelGroupList((cur) => cur.filter((w) => w.id !== v.id))}
                                style={{
                                  padding: "0",
                                }}
                              >
                                <FontAwesomeIcon icon={['fas', 'times']} />
                              </button>
                            </div>
                          ))
                        }
                      </div>
                    </div>
                  )
                }
                {
                  isLabelGroupWordLoading ? (
                    <LoadingSquare>
                      <>
                        プロジェクトデータを取得しています…
                      </>
                    </LoadingSquare>
                  ) : (
                    <>
                      {
                        !searchedLabelGroupList.filter((v) => !selectLabelGroupList.map((w) => w.id).includes(v.id)).length ? (
                          <>
                            <div
                              style={{
                                padding: "16px",
                                backgroundColor: "#f5f7ff",
                                textAlign: "center",
                                fontSize: "16px",
                                color: "#999999",
                                marginTop: "8px",
                              }}
                            >
                              {
                                searchLabelGroupWord.length ? (
                                  <>
                                    {
                                      isLabelGroupSearched ? (
                                        "キーワードに該当するプロジェクトがありませんでした"
                                      ) : (
                                        "検索中…"
                                      )
                                    }
                                  </>
                                ) : (
                                  "キーワードを入力して、プロジェクトを検索してください"
                                )
                              }
                            </div>
                          </>
                        ) : (
                          <>
                            {
                              searchedLabelGroupList.length === SEARCH_LIMIT && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexFlow: "column",
                                    alignItems: "center",
                                    gap: "4px",
                                    border: "solid 1px #FF0000",
                                    borderRadius: "4px",
                                    backgroundColor: "#fff2f2",
                                    color: "#FF0000",
                                    padding: "8px",
                                    marginBottom: "8px",
                                    marginTop: "8px",
                                  }}
                                >
                                  <div>
                                    {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                  </div>
                                  <div>
                                    お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                                  </div>
                                </div>
                              )
                            }
                            <div className="chip_list" style={{ marginTop: "8px" }}>
                              {
                                searchedLabelGroupList.filter((v) => !selectLabelGroupList.map((w) => w.id).includes(v.id)).map((v) => (
                                  <button
                                    type="button"
                                    className="chip"
                                    key={`labelGroup_${v.id}`}
                                    onClick={() => clickLabelGroup(v)}
                                  >
                                    {v.name}
                                  </button>
                                ))
                              }
                            </div>
                          </>
                        )
                      }
                    </>
                  )
                }
              </div>
            )
          }
          {
            masterRelationLabelGroup && (
              <div className="category">
                <div className="category_title">
                  プロジェクト名・プロジェクトコードで絞り込み
                </div>
                <label
                  htmlFor="master_relation_label_group_disabled"
                  className="disable_option"
                >
                  <input
                    id="master_relation_label_group_disabled"
                    type="checkbox"
                    className="icon_back"
                    checked={selectMasterRelationLabelGroupDisable}
                    onChange={(e) => setSelectMasterRelationLabelGroupDisable(e.target.checked)}
                  />
                  <FontAwesomeIcon
                    icon={['far', selectMasterRelationLabelGroupDisable ? 'check-square' : 'square']}
                    className={`icon ${selectMasterRelationLabelGroupDisable ? 'enabled' : 'disabled'}`}
                  />
                  <div className="text">
                    プロジェクトに紐づいていない業務のみ表示する
                  </div>
                </label>
                <div
                  style={{
                    position: "relative",
                  }}
                >
                  <input
                    type="text"
                    placeholder="キーワードでプロジェクト名を検索・選択してください"
                    onChange={(event) => setSearchMasterRelationLabelGroupWord(event.target.value)}
                    onCompositionStart={() => setMasterRelationLabelGroupComposition(true)}
                    onCompositionEnd={() => setMasterRelationLabelGroupComposition(false)}
                    value={searchMasterRelationLabelGroupWord}
                    style={{
                      width: "100%",
                      color: selectMasterRelationLabelGroupDisable ? "#999999" : undefined,
                      cursor: selectMasterRelationLabelGroupDisable ? "not-allowed" : undefined,
                      backgroundColor: selectMasterRelationLabelGroupDisable ? "#f5f5f5" : undefined,
                    }}
                    disabled={selectMasterRelationLabelGroupDisable}
                  />
                  {
                    !!searchMasterRelationLabelGroupWord.length && !selectMasterRelationLabelGroupDisable && (
                      <button
                        type="button"
                        className="reset"
                        onClick={() => setSearchMasterRelationLabelGroupWord("")}
                        disabled={selectMasterRelationLabelGroupDisable}
                      >
                        <FontAwesomeIcon icon={['far', 'times-circle']} />
                      </button>
                    )
                  }
                </div>
                {!selectMasterRelationLabelGroupDisable && (
                  <>
                    {
                      !!selectMasterRelationLabelGroupList.length && (
                        <div
                          style={{
                            border: "solid 1px #CCCCCC",
                            borderRadius: "4px",
                            marginTop: "8px",
                            padding: "4px",
                          }}
                        >
                          <div
                            style={{
                              padding: "4px",
                              borderBottom: "dashed 1px #CCCCCC",
                            }}
                          >
                            選択済み
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: "4px",
                              padding: "4px",
                            }}
                          >
                            {
                              selectMasterRelationLabelGroupList.map((v) => (
                                <div
                                  style={{
                                    border: "solid 1px #CCCCCC",
                                    borderRadius: "20px",
                                    padding: "2px 4px",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "2px",
                                    fontSize: "10px",
                                  }}
                                >
                                  {v.name}
                                  <button
                                    type="button"
                                    onClick={() => setSelectMasterRelationLabelGroupList((cur) => cur.filter((w) => w.id !== v.id))}
                                    style={{
                                      padding: "0",
                                    }}
                                  >
                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                  </button>
                                </div>
                              ))
                            }
                          </div>
                        </div>
                      )
                    }
                    {
                      isMasterRelationLabelGroupWordLoading ? (
                        <LoadingSquare>
                          <>
                            プロジェクトデータを取得しています…
                          </>
                        </LoadingSquare>
                      ) : (
                        <>
                          {
                            !searchedMasterRelationLabelGroupList
                              .filter((v) => !selectMasterRelationLabelGroupList.map((w) => w.id).includes(v.id)).length ? (
                                <>
                                  <div
                                    style={{
                                      padding: "16px",
                                      backgroundColor: "#f5f7ff",
                                      textAlign: "center",
                                      fontSize: "16px",
                                      color: "#999999",
                                      marginTop: "8px",
                                    }}
                                  >
                                    {
                                      searchMasterRelationLabelGroupWord.length ? (
                                        <>
                                          {
                                            isMasterRelationLabelGroupSearched ? (
                                              "キーワードに該当するプロジェクトがありませんでした"
                                            ) : (
                                              "検索中…"
                                            )
                                          }
                                        </>
                                      ) : (
                                        "キーワードを入力して、プロジェクトを検索してください"
                                      )
                                    }
                                  </div>
                                </>
                              ) : (
                                <>
                                  {
                                    searchedMasterRelationLabelGroupList.length === SEARCH_LIMIT && (
                                      <div
                                        style={{
                                          display: "flex",
                                          flexFlow: "column",
                                          alignItems: "center",
                                          gap: "4px",
                                          border: "solid 1px #FF0000",
                                          borderRadius: "4px",
                                          backgroundColor: "#fff2f2",
                                          color: "#FF0000",
                                          padding: "8px",
                                          marginBottom: "8px",
                                          marginTop: "8px",
                                        }}
                                      >
                                        <div>
                                          {`検索上位${SEARCH_LIMIT}件が表示されています`}
                                        </div>
                                        <div>
                                          お探しのプロジェクトが見つからない時は、キーワードを詳細に入力してください
                                        </div>
                                      </div>
                                    )
                                  }
                                  <div className="chip_list" style={{ marginTop: "8px" }}>
                                    {
                                      searchedMasterRelationLabelGroupList
                                        .filter((v) => !selectMasterRelationLabelGroupList.map((w) => w.id).includes(v.id)).map((v) => (
                                          <button
                                            type="button"
                                            className="chip"
                                            key={`labelGroup_${v.id}`}
                                            onClick={() => clickMasterRelationLabelGroup(v)}
                                          >
                                            {v.name}
                                          </button>
                                        ))
                                    }
                                  </div>
                                </>
                              )
                          }
                        </>
                      )
                    }
                  </>
                )}
              </div>
            )
          }
          {
            callbackColors ? (
              <div className="category">
                <div className="category_title">
                  カラーで絞り込み
                  {
                    selectColorList.length ? (
                      <div
                        className="select_reset"
                        role="none"
                        onClick={() => setSelectColorList([])}
                      >
                        リセット
                      </div>
                    ) : (
                      <></>
                    )
                  }
                </div>
                <div className="chip_list">
                  {
                    COLOR_LIST.map((colorCategory) => (
                      <React.Fragment key={`color_category_${colorCategory.name}`}>
                        {
                          colorCategory.values.map((color) => (
                            <button
                              type="button"
                              className={`chip ${selectColorList.includes(color.label) ? "checked" : ""}`}
                              key={`color_${color.text}`}
                              onClick={() => clickColor(color.label)}
                            >
                              <div
                                style={{
                                  backgroundColor: color.color,
                                  width: "12px",
                                  height: "12px",
                                  borderRadius: "50%",
                                }}
                              />
                              {color.text}
                            </button>
                          ))
                        }
                      </React.Fragment>
                    ))
                  }
                </div>
              </div>
            ) : (
              <></>
            )
          }
        </div>
        <div className="button_area" style={{ display: "flex", alignItems: "center", justifyContent: "center", marginTop: "16px", gap: "40px" }}>
          <button
            type="button"
            className="button cancel"
            onClick={resetSearch}
          >
            絞り込み条件をリセット
          </button>
          <button
            type="button"
            className="button"
            onClick={submitSearch}
          >
            絞り込む
          </button>
        </div>
      </div>
      <div className={`close_screen gray ${isSearchDisplay || 'none'}`} role="presentation" onClick={cancelSearch} />
    </>
  )
}

SearchModuleForHighVolume.defaultProps = {
  callbackLabelMasterKeywords: undefined,
  callbackTeamIds: undefined,
  callbackMemberIds: undefined,
  callbackLabelGroupIds: undefined,
  callbackColors: undefined,
  labelMasterKeywords: undefined,
  memberList: undefined,
  teamList: undefined,
  labelGroupList: undefined,
  colors: undefined,
  isClear: false,
  masterRelationLabelGroup: undefined,
}

export default SearchModuleForHighVolume
